import classNames from "classnames";
import { DrillProgressBarGroup } from "../../components/features/DrillProgressBarGroup";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverProvider,
  PopoverTrigger,
} from "../../components/general/Popover/Popover";
import { calcTotalCount, Progress } from "../../domains/DrillProgress";
import TimeHelper from "../../helpers/TimeHelper";

type Props = {
  drillSectionName: string;
  duration: number;
  progress: Progress;
  // Notee教材のセクションのみ非nullで返る書き込み欄の正解数(分子)/総数(分母)
  correctInputAreaCount?: number | null;
  totalInputAreaCount?: number | null;
  isGrayedOut: boolean;
};
export const RetentionLevelCell = ({
  drillSectionName,
  duration,
  progress,
  correctInputAreaCount,
  totalInputAreaCount,
  isGrayedOut,
}: Props) => {
  const totalCount = calcTotalCount(progress);
  const inputAreaFraction = formatInputAreaFraction(
    correctInputAreaCount,
    totalInputAreaCount,
  );

  return (
    <div className="flex h-full flex-col border-0 border-r border-solid border-gray-400">
      <DrillSectionName name={drillSectionName} isGrayedOut={isGrayedOut} />
      <div className="flex min-h-[4rem] flex-auto flex-col items-center justify-center border-0 border-t border-solid border-gray-400">
        <Duration duration={duration} isGrayedOut={isGrayedOut} />
        {inputAreaFraction !== null && (
          <InputAreaFraction
            value={inputAreaFraction}
            isGrayedOut={isGrayedOut}
          />
        )}
      </div>
      <div className={classNamesWithGrayOut("flex w-full", isGrayedOut)}>
        <DrillProgressBarGroup totalCount={totalCount} progress={progress} />
      </div>
    </div>
  );
};

// Notee教材の書き込み欄の分数表示 (DISP-14)。
// 非Notee(両カウントnull/undefined)は表示しない(null)、分母0は "-"、それ以外は "(正解/総数)"。
const formatInputAreaFraction = (
  correctInputAreaCount?: number | null,
  totalInputAreaCount?: number | null,
): string | null => {
  if (correctInputAreaCount == null || totalInputAreaCount == null) {
    return null;
  }
  if (totalInputAreaCount === 0) {
    return "-";
  }
  return `(${correctInputAreaCount}/${totalInputAreaCount})`;
};

const DrillSectionName = ({
  name,
  isGrayedOut,
}: {
  name: string;
  isGrayedOut: boolean;
}) => {
  return (
    <PopoverProvider>
      <Popover>
        <PopoverTrigger asChild className="cursor-pointer">
          <div
            className={classNamesWithGrayOut(
              "overflow-hidden text-ellipsis whitespace-nowrap px-1",
              isGrayedOut,
            )}
          >
            {name}
          </div>
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverContent side="top">
            <div className="max-w-[32rem] break-all text-center">{name}</div>
          </PopoverContent>
        </PopoverPortal>
      </Popover>
    </PopoverProvider>
  );
};

const Duration = ({
  duration,
  isGrayedOut,
}: {
  duration: number;
  isGrayedOut: boolean;
}) => {
  const formatedDuration = TimeHelper.secondsToDisplayTime(duration);
  return (
    <div className={classNamesWithGrayOut("", isGrayedOut)}>
      {formatedDuration}
    </div>
  );
};

// Notee教材の書き込み欄の分数(例 "(8/10)")。学習時間の直下・定着度バーの上に、
// 学習時間と同じ文字サイズで表示する (DISP-14)。
const InputAreaFraction = ({
  value,
  isGrayedOut,
}: {
  value: string;
  isGrayedOut: boolean;
}) => {
  return <div className={classNamesWithGrayOut("", isGrayedOut)}>{value}</div>;
};

const classNamesWithGrayOut = (className: string, isGrayedOut: boolean) =>
  classNames(className, { "opacity-50": isGrayedOut });
